/**
 * MobileTabBar — Bottom-Navigation für Mobile (md:hidden).
 *
 * Spec §8 (Mobile/PWA): 4 Items (Inbox, Rechnungen, Aktionen-Plus mittig
 * leicht angehoben, Mehr). 56px Hoehe + safe-area-bottom respect.
 *
 * Plus-FAB öffnet ein vaul-Drawer (Action-Sheet) mit Quick-Actions.
 * "Mehr" öffnet ein vaul-Drawer mit den restlichen Nav-Items
 * (Workflows, Lieferanten, Payment-Runs, Analytics, Bankkonten,
 * ggf. Admin-Bereiche).
 *
 * A11y: Touch-Targets >= 44px, aria-label auf allen Icon-Buttons,
 * aria-current="page" auf aktivem Tab via NavLink, Plus-FAB als Button
 * mit aria-haspopup="dialog".
 */
import { useState } from "react";
import { NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  BarChart3,
  Building2,
  CreditCard,
  FileText,
  Inbox as InboxIcon,
  LogOut,
  MoreHorizontal,
  Plus,
  Settings as SettingsIcon,
  ShieldCheck,
  Upload,
  Users as UsersIcon,
  Wallet,
  Workflow as WorkflowIcon,
} from "lucide-react";
import { Drawer } from "vaul";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { useMyApprovals } from "@/hooks/use-approvals";
import { useAuth } from "@/contexts/AuthContext";

interface TabItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number;
}

interface ActionItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  description: string;
  onClick: () => void;
}

interface MoreItem {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  to?: string;
  onClick?: () => void;
  destructive?: boolean;
}

export function MobileTabBar() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const reducedMotion = useReducedMotion();
  const { data: approvals = [] } = useMyApprovals(workspaceId || null);
  const { memberships } = useAuth();
  const isAdmin = memberships.some((m) => m.role === "OWNER" || m.role === "ADMIN");
  const [actionOpen, setActionOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  const base = `/workspaces/${workspaceId}`;

  // Spec §8: genau 4 Items (Inbox, Rechnungen, Plus-FAB, Mehr).
  // Workflows wandert in das Mehr-Drawer.
  const tabs: TabItem[] = [
    { to: `${base}/inbox`, label: "Inbox", icon: InboxIcon, badge: approvals.length },
    { to: `${base}/invoices`, label: "Rechnungen", icon: FileText },
  ];

  const goAndClose = (to: string, setOpen: (v: boolean) => void) => () => {
    setOpen(false);
    navigate(to);
  };

  const actions: ActionItem[] = [
    {
      icon: Upload,
      label: "Beleg hochladen",
      description: "PDF/XML/ZUGFeRD oder Foto",
      onClick: goAndClose(`${base}/invoices?upload=1`, setActionOpen),
    },
    {
      icon: CreditCard,
      label: "Payment-Run erstellen",
      description: "Neuen Zahlungslauf zusammenstellen",
      onClick: goAndClose(`${base}/payment-runs/new`, setActionOpen),
    },
    {
      icon: Building2,
      label: "Lieferant anlegen",
      description: "Neuen Kreditor erfassen",
      onClick: goAndClose(`${base}/suppliers?new=1`, setActionOpen),
    },
    {
      icon: WorkflowIcon,
      label: "Workflow erstellen",
      description: "Approval-Flow modellieren",
      onClick: goAndClose(`${base}/workflows/new`, setActionOpen),
    },
  ];

  const moreItems: MoreItem[] = [
    { icon: WorkflowIcon, label: "Workflows", to: `${base}/workflows` },
    { icon: Building2, label: "Lieferanten", to: `${base}/suppliers` },
    { icon: CreditCard, label: "Payment-Runs", to: `${base}/payment-runs` },
    { icon: BarChart3, label: "Analytics", to: `${base}/analytics` },
    { icon: Wallet, label: "Bankkonten", to: `${base}/bank-accounts` },
  ];
  if (isAdmin) {
    moreItems.push(
      { icon: UsersIcon, label: "Nutzer", to: `${base}/users` },
      { icon: SettingsIcon, label: "Einstellungen", to: `${base}/settings` },
      { icon: ShieldCheck, label: "Audit-Log", to: `${base}/audit` },
    );
  }
  moreItems.push({
    icon: LogOut,
    label: "Workspace wechseln",
    to: "/workspaces",
    destructive: false,
  });

  // Aktiver Tab für aria-current — basiert auf URL-Match.
  const isActive = (to: string) => location.pathname.startsWith(to);

  const tabClass = ({ active }: { active: boolean }) =>
    cn(
      "flex flex-1 flex-col items-center justify-center gap-0.5 py-1 text-[10px] font-medium transition-colors",
      "min-h-[44px]",
      active
        ? "text-brand-600 dark:text-brand-400"
        : "text-text-tertiary hover:text-text-primary",
    );

  return (
    <>
      <nav
        aria-label="Mobile Hauptnavigation"
        className={cn(
          "fixed bottom-0 left-0 right-0 z-40 md:hidden",
          "border-t border-border-default bg-bg-surface/95 backdrop-blur-md",
          "supports-[backdrop-filter]:bg-bg-surface/80",
        )}
        style={{
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
      >
        <div className="flex h-14 items-stretch">
          {/* Tab 1: Inbox */}
          <NavLink
            to={tabs[0].to}
            className={({ isActive: a }) => tabClass({ active: a || isActive(tabs[0].to) })}
          >
            {({ isActive: a }) => (
              <>
                <div className="relative">
                  <InboxIcon className="h-5 w-5" aria-hidden="true" />
                  {tabs[0].badge !== undefined && tabs[0].badge > 0 && (
                    <span
                      role="status"
                      aria-label={`${tabs[0].badge} offene Freigaben`}
                      className="absolute -right-2 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand-600 px-1 text-[9px] font-semibold text-white"
                    >
                      {tabs[0].badge > 99 ? "99+" : tabs[0].badge}
                    </span>
                  )}
                </div>
                <span className="leading-none">{tabs[0].label}</span>
                {a /* nur für A11y, NavLink setzt aria-current automatisch */ && null}
              </>
            )}
          </NavLink>

          {/* Tab 2: Rechnungen */}
          <NavLink
            to={tabs[1].to}
            className={({ isActive: a }) => tabClass({ active: a || isActive(tabs[1].to) })}
          >
            <FileText className="h-5 w-5" aria-hidden="true" />
            <span className="leading-none">{tabs[1].label}</span>
          </NavLink>

          {/* FAB - mittig, angehoben (Spec §8) */}
          <div className="relative flex w-16 items-start justify-center">
            <motion.button
              type="button"
              onClick={() => setActionOpen(true)}
              aria-label="Neue Aktion"
              aria-haspopup="dialog"
              aria-expanded={actionOpen}
              whileTap={reducedMotion ? undefined : { scale: 0.92 }}
              transition={{ duration: reducedMotion ? 0 : 0.12, ease: [0.16, 1, 0.3, 1] }}
              className={cn(
                "-mt-6 flex h-12 w-12 items-center justify-center rounded-full",
                "bg-brand-600 text-white shadow-md ring-2 ring-bg-surface",
                "hover:bg-brand-700 active:bg-brand-700",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-surface",
              )}
            >
              <Plus className="h-6 w-6" aria-hidden="true" />
            </motion.button>
          </div>

          {/* Tab 4: Mehr */}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            aria-label="Weitere Bereiche"
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
            className={tabClass({ active: false })}
          >
            <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
            <span className="leading-none">Mehr</span>
          </button>
        </div>
      </nav>

      {/* Action-Sheet — Plus-FAB */}
      <Drawer.Root open={actionOpen} onOpenChange={setActionOpen}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 z-50 bg-black/40" />
          <Drawer.Content
            className={cn(
              "fixed bottom-0 left-0 right-0 z-50 mt-24 flex flex-col rounded-t-2xl",
              "border border-border-default bg-bg-surface outline-none",
            )}
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
          >
            <Drawer.Title className="sr-only">Neue Aktion</Drawer.Title>
            <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-border-default" />
            <div className="px-4 pt-3 pb-2">
              <div className="text-base font-semibold tracking-tight text-text-primary">Neue Aktion</div>
              <div className="text-xs text-text-tertiary">Was möchtest du erstellen?</div>
            </div>
            <ul className="px-2 pb-3" role="list">
              {actions.map((a) => {
                const Icon = a.icon;
                return (
                  <li key={a.label}>
                    <button
                      type="button"
                      onClick={a.onClick}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors",
                        "hover:bg-bg-hover active:bg-bg-active",
                        "min-h-[56px]",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 focus-visible:ring-offset-bg-surface",
                      )}
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-tint text-brand-600 dark:text-brand-400">
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="text-sm font-medium text-text-primary">{a.label}</span>
                        <span className="text-xs text-text-tertiary">{a.description}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      {/* More-Drawer — restliche Navigation */}
      <Drawer.Root open={moreOpen} onOpenChange={setMoreOpen}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 z-50 bg-black/40" />
          <Drawer.Content
            className={cn(
              "fixed bottom-0 left-0 right-0 z-50 mt-24 flex max-h-[90vh] flex-col rounded-t-2xl",
              "border border-border-default bg-bg-surface outline-none",
            )}
            style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
          >
            <Drawer.Title className="sr-only">Weitere Navigation</Drawer.Title>
            <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-border-default" />
            <div className="px-4 pt-3 pb-2">
              <div className="text-base font-semibold tracking-tight text-text-primary">Mehr</div>
              <div className="text-xs text-text-tertiary">Weitere Bereiche und Einstellungen</div>
            </div>
            <ul className="overflow-y-auto px-2 pb-3" role="list">
              {moreItems.map((it) => {
                const Icon = it.icon;
                const handle = () => {
                  setMoreOpen(false);
                  if (it.to) navigate(it.to);
                  else if (it.onClick) it.onClick();
                };
                return (
                  <li key={it.label}>
                    <button
                      type="button"
                      onClick={handle}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors",
                        "hover:bg-bg-hover active:bg-bg-active",
                        "min-h-[48px]",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 focus-visible:ring-offset-bg-surface",
                        it.destructive && "text-status-rejected",
                      )}
                    >
                      <Icon className="h-5 w-5 shrink-0 text-text-tertiary" />
                      <span className="text-sm font-medium text-text-primary">{it.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </>
  );
}
